/**
 * PaymentStatusPanel - the register header pill + dropdown of
 * docs/POS_PAYMENT_STATUS_PANEL.md, against the panel endpoints of
 * docs/CHECKOUT_API_2026-10-28.md §5.
 *
 *   const status = usePaymentStatusPanel(checkoutPanelFetcher({ token, terminalId }));
 *   <PaymentStatusPanel status={status} canManage={role !== 'staff'} onChooseTerminal={setDefault} />
 *
 * or let it own the hook:  <PaymentStatusPanel fetchPanel={checkoutPanelFetcher({...})} />
 *
 * `PaymentStatusDetails` is the dropdown body alone, for a set-up / status screen
 * (the kiosk shows it on its set-up screen).
 *
 * Words: "Aster Gateway", "Gateway account ID", "Card terminal", "Terminal ID" -
 * never a processor name. Every state is the gateway's own reading.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, CreditCard, Loader2, RefreshCw } from 'lucide-react';
import { boPath } from '@/utils/getBasePath';
import { formatTime, parseApiTime } from '@/lib/restaurantTime';
import { usePaymentStatusPanel, summarizePanel } from './usePaymentStatusPanel';

const TONE = {
  online: { dot: 'bg-emerald-400', text: 'text-emerald-300', ring: 'border-emerald-400/40 bg-emerald-400/10' },
  offline: { dot: 'bg-red-400', text: 'text-red-300', ring: 'border-red-400/40 bg-red-400/10' },
  unknown: { dot: 'bg-slate-400', text: 'text-slate-300', ring: 'border-white/15 bg-white/5' },
  checking: { dot: 'bg-sky-400 animate-pulse', text: 'text-sky-300', ring: 'border-sky-400/30 bg-sky-400/10' },
};

// theme="light" (the warm checkout surface): same states, light colours.
const TONE_LIGHT = {
  online: { dot: 'bg-emerald-500', text: 'text-emerald-700', ring: 'border-emerald-500 bg-emerald-50' },
  offline: { dot: 'bg-red-500', text: 'text-red-700', ring: 'border-red-500 bg-red-50' },
  unknown: { dot: 'bg-stone-400', text: 'text-stone-600', ring: 'border-stone-400 bg-stone-100' },
  checking: { dot: 'bg-sky-500 animate-pulse', text: 'text-sky-700', ring: 'border-dashed border-stone-400 bg-white' },
};

const LightCtx = React.createContext(false);
const useTone = () => (React.useContext(LightCtx) ? TONE_LIGHT : TONE);
const useLight = () => React.useContext(LightCtx);

const STATE_LABEL = { online: 'Online', offline: 'Offline', unknown: 'Unknown' };
const ACCOUNT_LABEL = { connected: 'Connected', not_connected: 'Not connected', unknown: 'Unknown' };
const ACCOUNT_TONE = { connected: 'online', not_connected: 'offline', unknown: 'unknown' };
const CHANNEL_LABEL = { ready: 'Ready', not_enabled: 'Not enabled' };

/** Back office Payment Settings: the app. subdomain from a PWA subdomain, else this host's back office. */
export function defaultManageHref() {
  const host = window.location.hostname;
  const parts = host.split('.');
  const local = host.includes('localhost') || host.includes('ondigitalocean.app');
  if (!local && parts.length >= 3 && parts[0] !== 'app') {
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${window.location.protocol}//app.${parts.slice(1).join('.')}${port}/payment-setup`;
  }
  return boPath('payment-setup');
}

// on the restaurant's clock (the panel answer's `timezone`), never the device's
function timeOf(iso, tz) {
  if (!parseApiTime(iso)) return null;
  return formatTime(iso, tz);
}

function Dot({ tone }) {
  const T = useTone();
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${T[tone]?.dot || T.unknown.dot}`} aria-hidden="true" />;
}

function Row({ title, value, tone, sub, testId, children }) {
  const T = useTone();
  const light = useLight();
  return (
    <div className={`px-4 py-3 border-b last:border-b-0 ${light ? 'border-stone-200' : 'border-white/[0.06]'}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <span className={`text-sm ${light ? 'font-bold text-stone-900' : 'font-medium text-white/90'}`}>{title}</span>
        {value && (
          <span className={`flex items-center gap-2 text-sm font-semibold ${T[tone]?.text || (light ? 'text-stone-600' : 'text-white/70')}`}>
            <Dot tone={tone} />{value}
          </span>
        )}
      </div>
      {sub && <p className={`mt-1 text-xs leading-relaxed ${light ? 'text-stone-500' : 'text-white/50'}`}>{sub}</p>}
      {children}
    </div>
  );
}

function TerminalRow({ t, onChooseTerminal }) {
  const TONE = useTone();
  const light = useLight();
  const tone = STATE_LABEL[t.state] ? t.state : 'unknown';
  const facts = [
    t.terminal_id ? `Terminal ID ${t.terminal_id}` : null,
    t.equipment || null,
    t.serial ? `Serial ${t.serial}` : null,
  ].filter(Boolean);
  return (
    <div className={`mt-2 rounded-xl border p-3 ${light ? (t.is_this_register ? 'border-orange-500 border-[1.5px] bg-orange-50' : 'border-stone-200 bg-white') : 'border-white/[0.08] bg-white/[0.03]'}`} data-testid={`panel-terminal-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-sm font-semibold truncate ${light ? 'text-stone-900' : 'text-white'}`}>
            {t.label || 'Card terminal'}
            {t.is_this_register && (
              <span className={`ml-2 align-middle text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${light ? 'bg-stone-900 text-white' : 'bg-sky-400/15 text-sky-300'}`}
                data-testid="panel-this-register">this register</span>
            )}
          </p>
          {facts.length > 0 && <p className={`text-xs mt-0.5 ${light ? 'text-stone-500' : 'text-white/50'}`}>{facts.join(' · ')}</p>}
        </div>
        <span className={`flex items-center gap-1.5 text-xs font-semibold shrink-0 ${TONE[tone].text}`} data-testid="panel-terminal-state">
          <Dot tone={tone} />{STATE_LABEL[tone]}
        </span>
      </div>
      {t.reason && <p className={`text-xs mt-1.5 leading-relaxed ${light ? 'text-stone-500' : 'text-white/55'}`} data-testid="panel-terminal-reason">{t.reason}</p>}
      {onChooseTerminal && !t.is_this_register && (
        <button type="button" onClick={() => onChooseTerminal(t)}
          className={`mt-2 min-h-[36px] text-xs font-semibold ${light ? 'text-orange-700 hover:text-orange-800' : 'text-sky-300 hover:text-sky-200'}`} data-testid={`panel-choose-${t.id}`}>
          Use this terminal for this register
        </button>
      )}
    </div>
  );
}

/** The dropdown body (account, terminals, channels, manage link). */
export function PaymentStatusDetails({ status, canManage = false, manageHref, onManage, onChooseTerminal, className = '', theme }) {
  const inherited = useLight();
  if (theme === 'light' && !inherited) {
    return (
      <LightCtx.Provider value>
        <PaymentStatusDetails status={status} canManage={canManage} manageHref={manageHref} onManage={onManage}
          onChooseTerminal={onChooseTerminal} className={className} />
      </LightCtx.Provider>
    );
  }
  return <Details status={status} canManage={canManage} manageHref={manageHref} onManage={onManage} onChooseTerminal={onChooseTerminal} className={className} light={inherited} />;
}

function Details({ status, canManage, manageHref, onManage, onChooseTerminal, className, light }) {
  const { panel, checking, error, refresh } = status;
  const account = panel?.account || {};
  const accountState = ACCOUNT_LABEL[account.state] ? account.state : 'unknown';
  const terminals = panel?.terminals || [];
  const channels = panel?.channels || {};
  const checkedAt = timeOf(panel?.checked_at, panel?.timezone);
  const href = manageHref || defaultManageHref();

  const terminalTone = (() => {
    const s = summarizePanel(panel, { error });
    return s.tone === 'checking' ? 'unknown' : s.tone;
  })();

  return (
    <div className={`text-left ${className}`} data-testid="payment-status-details">
      <Row title="Payment account" value={panel ? ACCOUNT_LABEL[accountState] : (checking ? 'Checking...' : 'Unknown')}
        tone={panel ? ACCOUNT_TONE[accountState] : (checking ? 'checking' : 'unknown')} testId="panel-account"
        sub={account.gateway_account_id ? `Aster Gateway · Gateway account ID ${account.gateway_account_id}`
          : (account.reason || (panel ? 'Aster Gateway' : error))} />

      <Row title={terminals.length > 1 ? `Card terminals (${terminals.length})` : 'Card terminal'}
        value={terminals.length === 1 ? STATE_LABEL[terminals[0].state] || 'Unknown' : (terminals.length ? null : 'None')}
        tone={terminals.length === 1 ? terminalTone : 'unknown'} testId="panel-terminals"
        sub={!terminals.length && panel ? 'No card terminal is set up here yet.' : null}>
        {terminals.map((t) => <TerminalRow key={t.id} t={t} onChooseTerminal={onChooseTerminal} />)}
      </Row>

      {[['qr', 'QR'], ['payment_link', 'Payment Link'], ['saved_card', 'Saved Card']].map(([key, title]) => {
        const v = channels[key];
        const label = CHANNEL_LABEL[v] || (panel ? 'Unknown' : '—');
        // QR / Payment Link follow online ordering: a virtual terminal (or the gateway's ecommerce) - the server says why not
        const why = v === 'not_enabled' ? panel?.channel_reasons?.[key] : null;
        return <Row key={key} title={title} value={label} tone={v === 'ready' ? 'online' : 'unknown'} testId={`panel-channel-${key}`}
          sub={why || null} />;
      })}

      {error && panel && (
        <p className={`px-4 pt-3 text-xs ${light ? 'text-amber-700' : 'text-amber-300/90'}`} data-testid="panel-error">{error} Showing the last reading.</p>
      )}
      {panel?.error && (
        <p className={`px-4 pt-3 text-xs ${light ? 'text-amber-700' : 'text-amber-300/90'}`}>{panel.error}</p>
      )}

      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <span className={`text-xs ${light ? 'text-stone-400' : 'text-white/40'}`}>{checkedAt ? `Checked ${checkedAt} · refreshes every 60 s` : ''}</span>
        <button type="button" onClick={refresh} disabled={checking}
          className={`flex items-center gap-1.5 min-h-[36px] text-xs font-semibold disabled:opacity-60 ${light ? 'text-stone-600 hover:text-stone-900' : 'text-white/70 hover:text-white'}`}
          data-testid="panel-refresh">
          {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {checking ? 'Checking...' : 'Refresh'}
        </button>
      </div>

      {canManage && (
        <a href={href} onClick={onManage ? (e) => { e.preventDefault(); onManage(); } : undefined}
          className={`flex items-center justify-between px-4 py-3 border-t text-sm rounded-b-2xl ${light ? 'border-stone-200 font-bold text-stone-900 hover:bg-stone-50' : 'border-white/[0.08] font-semibold text-sky-300 hover:bg-white/[0.04]'}`}
          data-testid="panel-manage">
          Manage Payment Settings <ChevronRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function OwnHook(props) {
  const status = usePaymentStatusPanel(props.fetchPanel);
  return <PillAndDropdown {...props} status={status} />;
}

function PillAndDropdown({ status, align = 'right', className = '', theme, ...rest }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const summary = status.summary;
  const light = theme === 'light';
  const T = light ? TONE_LIGHT : TONE;
  const tone = T[summary.tone] || T.unknown;

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const toggle = () => {
    if (!open) status.refresh();
    setOpen((v) => !v);
  };

  return (
    <LightCtx.Provider value={light}>
    <div ref={wrap} className={`relative inline-block ${className}`}>
      <button type="button" onClick={toggle} title={summary.reason || summary.label}
        aria-haspopup="dialog" aria-expanded={open}
        className={light
          ? `flex items-center gap-2 h-11 pl-2.5 pr-3 rounded-xl border-[1.5px] ${tone.ring} transition-colors hover:brightness-95`
          : `flex items-center gap-2 h-9 pl-2 pr-2.5 rounded-full border ${tone.ring} transition-colors hover:brightness-125`}
        data-testid="payment-status-pill" data-tone={summary.tone}>
        <span className="relative flex items-center">
          <CreditCard className={light ? `w-5 h-5 ${tone.text}` : 'w-4 h-4 text-white/80'} />
          {summary.count != null && (
            <span className={`absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${light ? 'bg-blue-600 text-white' : 'bg-white text-slate-900'}`}
              data-testid="payment-status-count">{summary.count}</span>
          )}
        </span>
        <span className={`flex items-center gap-1.5 font-semibold ${light ? 'text-sm font-bold' : 'text-xs'} ${tone.text}`}>
          {summary.tone === 'checking' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Dot tone={summary.tone} />}
          <span data-testid="payment-status-label">{summary.label}</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${light ? tone.text : 'text-white/50'} ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="dialog" aria-label="Payment status"
          className={light
            ? `absolute z-50 mt-2 w-[400px] max-w-[calc(100vw-24px)] rounded-[18px] border border-stone-200 bg-white text-stone-900 shadow-[0_24px_60px_rgba(28,25,23,.22)] ${align === 'left' ? 'left-0' : 'right-0'}`
            : `absolute z-50 mt-2 w-[340px] max-w-[calc(100vw-24px)] rounded-2xl border border-white/10 bg-[#141922]/95 backdrop-blur-xl shadow-2xl shadow-black/50 ${align === 'left' ? 'left-0' : 'right-0'}`}
          data-testid="payment-status-dropdown">
          <PaymentStatusDetails status={status} {...rest} />
        </div>
      )}
    </div>
    </LightCtx.Provider>
  );
}

/** Header pill + dropdown. Pass `status` (from usePaymentStatusPanel) or `fetchPanel`. */
export default function PaymentStatusPanel(props) {
  if (props.status) return <PillAndDropdown {...props} />;
  return <OwnHook {...props} />;
}
